import { Shield, AlertTriangle, CheckCircle2, Clock, AlertOctagon } from 'lucide-react';
import React from 'react';
import { useGetSafetySummaryQuery } from '../../services/dashboardApi';
import { WidgetProps } from './types';
import { WidgetLoadingSkeleton, WidgetEmptyState } from './WidgetErrorBoundary';

const SafetyWidget: React.FC<WidgetProps> = ({ scope, projectId, size }) => {
  const { data, isLoading, error } = useGetSafetySummaryQuery(
    { projectId: projectId! },
    { skip: scope !== 'project' || !projectId, pollingInterval: 300000 }
  );

  if (scope !== 'project') {
    return (
      <WidgetEmptyState
        title="Project Required"
        description="Select a project to view safety metrics"
        icon={Shield}
      />
    );
  }

  if (isLoading) {
    return <WidgetLoadingSkeleton lines={4} />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-600 text-sm font-medium">Failed to load safety data</p>
          <p className="text-gray-500 text-xs mt-1">Please try refreshing</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <WidgetEmptyState
        title="No Safety Data"
        description="Safety information is not available"
        icon={Shield}
      />
    );
  }

  const isCompact = size === '1x1';
  const safetyScoreColor =
    data.safetyScore >= 90
      ? 'text-green-600 bg-green-50'
      : data.safetyScore >= 70
        ? 'text-yellow-600 bg-yellow-50'
        : 'text-red-600 bg-red-50';

  return (
    <div className="space-y-4">
      {/* Safety Score & Days Without Incident */}
      <div className="grid grid-cols-2 gap-3">
        <div className={`rounded-lg p-3 ${safetyScoreColor}`}>
          <div className="flex items-center space-x-2 mb-1">
            <Shield className="h-4 w-4" />
            <span className="text-xs font-medium opacity-80">Safety Score</span>
          </div>
          <p className="text-2xl font-bold">{data.safetyScore}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="text-xs font-medium text-gray-600">Incident-Free</span>
          </div>
          <p className="text-2xl font-bold text-green-700">{data.daysWithoutIncident}d</p>
        </div>
      </div>

      {/* Incident Overview */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-red-50 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <AlertOctagon className="h-4 w-4 text-red-600" />
            <span className="text-xs font-medium text-gray-600">Open</span>
          </div>
          <p className="text-xl font-bold text-red-700">{data.openIncidents}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <CheckCircle2 className="h-4 w-4 text-gray-600" />
            <span className="text-xs font-medium text-gray-600">Resolved</span>
          </div>
          <p className="text-xl font-bold text-gray-700">{data.resolvedIncidents}</p>
        </div>
      </div>

      {/* Inspections Due */}
      {data.inspectionsDue > 0 && (
        <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <Clock className="h-4 w-4 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-700">
              {data.inspectionsDue} inspection{data.inspectionsDue !== 1 ? 's' : ''} due
            </span>
          </div>
        </div>
      )}

      {/* Severity Breakdown (for larger sizes) */}
      {!isCompact && data.bySeverity && (
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs font-semibold text-gray-700 mb-2">By Severity</p>
          <div className="grid grid-cols-4 gap-2">
            <div className="text-center">
              <div className="w-3 h-3 rounded-full bg-red-600 mx-auto mb-1" />
              <p className="text-xs text-gray-500">Critical</p>
              <p className="text-sm font-bold text-gray-900">{data.bySeverity.critical}</p>
            </div>
            <div className="text-center">
              <div className="w-3 h-3 rounded-full bg-orange-500 mx-auto mb-1" />
              <p className="text-xs text-gray-500">High</p>
              <p className="text-sm font-bold text-gray-900">{data.bySeverity.high}</p>
            </div>
            <div className="text-center">
              <div className="w-3 h-3 rounded-full bg-yellow-500 mx-auto mb-1" />
              <p className="text-xs text-gray-500">Medium</p>
              <p className="text-sm font-bold text-gray-900">{data.bySeverity.medium}</p>
            </div>
            <div className="text-center">
              <div className="w-3 h-3 rounded-full bg-green-500 mx-auto mb-1" />
              <p className="text-xs text-gray-500">Low</p>
              <p className="text-sm font-bold text-gray-900">{data.bySeverity.low}</p>
            </div>
          </div>
        </div>
      )}

      {/* Total Incidents Summary */}
      <div className="pt-2 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Total Incidents:{' '}
          <span className="font-semibold text-gray-900">{data.totalIncidents}</span>
        </p>
      </div>
    </div>
  );
};

export default SafetyWidget;
